import { forwardRef, useEffect, useCallback, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** モーダルの表示状態 */
  isOpen: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
  /** タイトル */
  title?: ReactNode;
  /** モーダルのサイズ */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** フッター */
  footer?: ReactNode;
}

/** @graph-connects none */
const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business 汎用モーダル。sm/md/lg/xlサイズ、ガラスモーフィズム背景、Escキー/オーバーレイクリック閉じ、ヘッダー・フッタースロット対応のPortalベースダイアログ
 * @graph-connects none
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ isOpen, onClose, title, size = 'md', footer, className = '', children, ...props }, ref) => {
    const { t } = useTranslation();

    // Escキーで閉じる
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      },
      [onClose],
    );

    useEffect(() => {
      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    const modalContent = (
      <div
        className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {/* オーバーレイ（ガラスモーフィズム背景） */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* モーダルコンテンツ */}
        <div
          ref={ref}
          className={`
            relative w-full
            bg-[var(--color-bg-surface)]
            border border-[var(--color-border)]
            rounded-xl shadow-xl
            animate-fade-in-up
            max-h-[85vh] flex flex-col
            ${sizeStyles[size]}
            ${className}
          `}
          {...props}
        >
          {/* ヘッダー */}
          {title != null && (
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-[var(--color-border)]">
              <div className="flex items-start justify-between gap-4">
                <h2
                  id="modal-title"
                  className="text-lg font-semibold text-[var(--color-text-primary)]"
                >
                  {title}
                </h2>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
                  aria-label={t('common.close')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* ボディ */}
          <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

          {/* フッター */}
          {footer && (
            <div className="flex-shrink-0 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] rounded-b-xl">
              {footer}
            </div>
          )}
        </div>
      </div>
    );

    return createPortal(modalContent, document.body);
  },
);

Modal.displayName = 'Modal';
